/**
 * BaseChart - Composant de base unifié pour tous les graphiques ApexCharts
 *
 * Gère le rendu de tous les types de graphiques (line, bar, area, pie, donut, etc.)
 * avec une configuration simplifiée et cohérente.
 */

import React, { Suspense, lazy, useMemo } from "react";
import { ApexOptions } from "apexcharts";
import { Typography, Loading } from "@/components/ui";
import { useChartColors } from "@/hooks/use-chart-colors";
import { useWorkspaceTheme } from "@/hooks/use-workspace-theme";
import type { ChartData, ChartVisualConfig } from "@/models/widget-v2";
import { chartColorsArray } from "@/styles/colors";

const Chart = lazy(() => import("react-apexcharts"));

// ============================================================================
// Types
// ============================================================================

export type ChartType =
  | "line"
  | "bar"
  | "area"
  | "pie"
  | "donut"
  | "radialBar"
  | "scatter"
  | "heatmap"
  | "treemap";

interface BaseChartProps {
  type: ChartType;
  data: ChartData;
  visual: ChartVisualConfig;
  height?: string | number;
  width?: string | number;
  onDataPointClick?: (label: string, value: number, dataIndex: number) => void;
}

// ============================================================================
// Helpers
// ============================================================================

const DEFAULT_COLORS = [...chartColorsArray];

/**
 * Résout les couleurs CSS variables en valeurs hex
 */
function resolveColors(colors: string[]): string[] {
  if (typeof window === "undefined") return DEFAULT_COLORS;

  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);

  return colors.map((color, index) => {
    if (color.startsWith("var(")) {
      // Extraire le nom de la variable CSS
      const match = color.match(/var\(([^,)]+)/);
      if (match) {
        const varName = match[1].trim();
        const resolved = computedStyle.getPropertyValue(varName).trim();
        if (resolved) return resolved;
      }
      // Fallback
      return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    }
    return color;
  });
}

/**
 * Construit les options ApexCharts selon le type de graphique
 */
function buildChartOptions(
  type: ChartType,
  data: ChartData,
  visual: ChartVisualConfig,
  themeColors: string[],
  isDark: boolean,
): ApexOptions {
  const colors = resolveColors(
    visual.colors?.length ? visual.colors : themeColors,
  );

  // Couleurs adaptées au thème
  const textColor = isDark ? "#e5e7eb" : "#374151"; // gray-200 / gray-700
  const gridColor = isDark ? "#374151" : "#e5e7eb"; // gray-700 / gray-200

  // Options de base communes
  const baseOptions: ApexOptions = {
    chart: {
      type: type === "donut" ? "donut" : type,
      toolbar: { show: false },
      animations: {
        enabled: visual.animation ?? true,
        speed: 800,
        animateGradually: { enabled: true, delay: 150 },
      },
      background: "transparent",
      foreColor: textColor,
      height: "100%",
      width: "100%",
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      sparkline: {
        enabled: false,
      },
    },
    colors,
    tooltip: {
      enabled: visual.tooltips ?? true,
      theme: isDark ? "dark" : "light",
    },
    legend: {
      show: visual.showLegend ?? true,
      position: visual.legendPosition || "top",
      fontSize: "12px",
      labels: {
        colors: textColor,
      },
    },
    dataLabels: {
      enabled: visual.showDataLabels ?? false,
      style: {
        colors: [isDark ? "#ffffff" : "#1f2937"],
      },
    },
    grid: {
      borderColor: gridColor,
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
  };

  // Options spécifiques selon le type
  switch (type) {
    case "line":
    case "area":
      return {
        ...baseOptions,
        stroke: {
          curve: visual.curveType || "smooth",
          width: visual.strokeWidth || 2,
        },
        fill: {
          type: type === "area" ? "gradient" : "solid",
          opacity: type === "area" ? (visual.fillOpacity ?? 0.3) : 1,
          gradient:
            type === "area"
              ? {
                  shadeIntensity: 1,
                  opacityFrom: visual.fillOpacity ?? 0.4,
                  opacityTo: 0.1,
                  stops: [0, 90, 100],
                }
              : undefined,
        },
        markers: {
          size: visual.showMarkers ? 4 : 0,
          strokeWidth: 2,
        },
        xaxis: {
          categories: data.labels,
          labels: {
            style: { fontSize: "12px", colors: textColor },
          },
          axisBorder: { show: true },
          axisTicks: { show: true },
        },
        yaxis: {
          labels: {
            style: { fontSize: "12px", colors: textColor },
          },
          axisBorder: { show: true },
          axisTicks: { show: true },
        },
        grid: {
          show: visual.showGrid ?? true,
          borderColor: gridColor,
          padding: {
            top: 10,
            right: 10,
            bottom: 0,
            left: 10,
          },
        },
      };

    case "bar":
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          stacked: visual.stacked ?? false,
        },
        plotOptions: {
          bar: {
            horizontal: visual.horizontal ?? false,
            columnWidth: visual.stacked ? "70%" : "60%",
            distributed: false,
          },
        },
        xaxis: {
          categories: data.labels,
          labels: {
            style: { fontSize: "12px", colors: textColor },
          },
          axisBorder: { show: true },
          axisTicks: { show: true },
        },
        yaxis: {
          labels: {
            style: { fontSize: "12px", colors: textColor },
          },
          axisBorder: { show: true },
          axisTicks: { show: true },
        },
        grid: {
          show: visual.showGrid ?? true,
          borderColor: gridColor,
          padding: {
            top: 10,
            right: 10,
            bottom: 0,
            left: 10,
          },
        },
        fill: {
          opacity: 0.9,
        },
      };

    case "pie":
    case "donut":
      // S'assurer que les labels sont définis et correspondent aux données
      const pieLabels =
        data.labels && data.labels.length > 0
          ? data.labels.map((l) => l || "Unknown")
          : (data.datasets[0]?.data || []).map((_, i) => `Item ${i + 1}`);

      return {
        ...baseOptions,
        labels: pieLabels,
        plotOptions: {
          pie: {
            donut: {
              size: type === "donut" ? `${visual.innerRadius || 50}%` : "0%",
              labels: {
                show: true,
                name: {
                  show: true,
                  color: textColor,
                },
                value: {
                  show: true,
                  color: textColor,
                },
                total: {
                  show: type === "donut",
                  color: textColor,
                },
              },
            },
          },
        },
        stroke: {
          width: 0,
        },
      };

    case "scatter":
      return {
        ...baseOptions,
        xaxis: {
          type: "numeric",
          labels: { style: { fontSize: "12px", colors: textColor } },
        },
        yaxis: {
          labels: { style: { fontSize: "12px", colors: textColor } },
        },
        grid: {
          show: visual.showGrid ?? true,
          borderColor: gridColor,
          padding: {
            top: 10,
            right: 10,
            bottom: 0,
            left: 10,
          },
        },
        markers: {
          size: 6,
        },
      };

    case "heatmap":
      return {
        ...baseOptions,
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            colorScale: {
              ranges: [
                { from: 0, to: 25, color: colors[0] || "#6366F1" },
                { from: 26, to: 50, color: colors[1] || "#818CF8" },
                { from: 51, to: 75, color: colors[2] || "#4F46E5" },
                { from: 76, to: 100, color: colors[3] || "#A5B4FC" },
              ],
            },
          },
        },
        xaxis: {
          categories: data.labels,
          labels: { style: { colors: textColor } },
        },
        yaxis: {
          labels: { style: { colors: textColor } },
        },
      };

    case "treemap":
      return {
        ...baseOptions,
        plotOptions: {
          treemap: {
            distributed: true,
            enableShades: true,
          },
        },
      };

    default:
      return baseOptions;
  }
}

/**
 * Construit les séries ApexCharts selon le type de graphique
 */
function buildSeries(
  type: ChartType,
  data: ChartData,
): ApexAxisChartSeries | number[] {
  if (type === "pie" || type === "donut") {
    // Pour pie/donut, on utilise le premier dataset
    // S'assurer que toutes les valeurs sont des nombres valides et positifs
    const rawData = data.datasets[0]?.data || [];
    return rawData.map((val) => {
      const num = typeof val === "number" ? val : Number(val);
      // ApexCharts pie/donut n'accepte que des nombres positifs ou 0
      return isNaN(num) || num < 0 ? 0 : num;
    });
  }

  // Pour treemap, convertir au format {x, y} attendu par ApexCharts
  if (type === "treemap") {
    const labels = data.labels || [];
    const values = data.datasets[0]?.data || [];
    return [
      {
        data: labels.map((label, index) => ({
          x: label,
          y:
            typeof values[index] === "number"
              ? values[index]
              : Number(values[index]) || 0,
        })),
      },
    ];
  }

  // Pour les autres types, on utilise le format standard
  return data.datasets.map((dataset) => ({
    name: dataset.label,
    data: dataset.data,
  }));
}

// ============================================================================
// Loading & Empty States
// ============================================================================

const LoadingState: React.FC = () => (
  <div className="h-full w-full flex items-center justify-center">
    <Loading size={32} />
  </div>
);

const EmptyState: React.FC = () => (
  <div className="h-full w-full flex flex-col items-center justify-center text-content-tertiary">
    <Typography variant="text-sm">Aucune donnée à afficher</Typography>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

export const BaseChart: React.FC<BaseChartProps> = ({
  type,
  data,
  visual,
  height = "100%",
  width = "100%",
  onDataPointClick,
}) => {
  // Récupérer les couleurs du thème
  const themeColors = useChartColors();
  const { isNight } = useWorkspaceTheme();

  // Vérifier si les données sont valides
  const hasValidData = useMemo(() => {
    if (!data.datasets || data.datasets.length === 0) return false;

    // Pour pie/donut, vérifier que la somme des valeurs est > 0
    if (type === "pie" || type === "donut") {
      const values = data.datasets[0]?.data || [];
      if (values.length === 0) return false;
      const sum = values.reduce((acc, val) => {
        const num = typeof val === "number" ? val : Number(val);
        return acc + (isNaN(num) ? 0 : num);
      }, 0);
      return sum > 0;
    }

    return data.datasets.some((d) => d.data && d.data.length > 0);
  }, [data, type]);

  // Construire les options
  const options = useMemo(() => {
    const opts = buildChartOptions(type, data, visual, themeColors.primary, isNight);

    if (onDataPointClick) {
      opts.chart = {
        ...opts.chart,
        events: {
          ...opts.chart?.events,
          dataPointSelection: (_event: any, _chartContext: any, config: { dataPointIndex: number }) => {
            const index = config.dataPointIndex;
            const label = data.labels?.[index] ?? "";
            const value = data.datasets[0]?.data?.[index] ?? 0;
            onDataPointClick(label, value, index);
          },
        },
      };
    }

    return opts;
  }, [type, data, visual, themeColors, isNight, onDataPointClick]);

  // Construire les séries
  const series = useMemo(() => {
    return buildSeries(type, data);
  }, [type, data]);

  // Afficher l'état vide si pas de données
  if (!hasValidData) {
    return (
      <div className="h-full w-full flex flex-col" style={{ minHeight: 200 }}>
        {(visual.title || visual.subtitle) && (
          <div className="px-4 pt-4 pb-2 flex-none">
            {visual.title && (
              <Typography variant="h6" className="text-content-primary">
                {visual.title}
              </Typography>
            )}
            {visual.subtitle && (
              <Typography variant="text-sm" className="text-content-tertiary">
                {visual.subtitle}
              </Typography>
            )}
          </div>
        )}
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <EmptyState />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col" style={{ minHeight: 200 }}>
      {/* Titre et sous-titre */}
      {(visual.title || visual.subtitle) && (
        <div className="px-4 pt-4 pb-2 flex-none">
          {visual.title && (
            <Typography variant="h6" className="text-content-primary">
              {visual.title}
            </Typography>
          )}
          {visual.subtitle && (
            <Typography variant="text-sm" className="text-content-tertiary">
              {visual.subtitle}
            </Typography>
          )}
        </div>
      )}

      {/* Graphique */}
      <div className={`flex-1 min-h-0 px-2 pb-2${onDataPointClick ? " cursor-pointer" : ""}`}>
        <Suspense fallback={<LoadingState />}>
          <Chart
            options={options}
            series={series}
            type={type === "donut" ? "donut" : type}
            height="100%"
            width="100%"
          />
        </Suspense>
      </div>
    </div>
  );
};

export default BaseChart;
